import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import AmlReportCard, { zoneOf } from "@/components/AmlReportCard";

const short = (a) => {
  const s = String(a || "");
  return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
};

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
};

const ZoneIcon = ({ zone }) => {
  if (zone === "high") return <ShieldX className="w-5 h-5 text-red-600" />;
  if (zone === "medium") return <ShieldAlert className="w-5 h-5 text-amber-600" />;
  if (zone === "unknown") return <ShieldAlert className="w-5 h-5 text-muted-foreground" />;
  return <ShieldCheck className="w-5 h-5 text-emerald-600" />;
};

const AmlHistoryPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const res = await api.get("/aml/history?limit=50");
        if (alive) setItems(res.data?.items || []);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, retry]);

  if (selected) {
    return (
      <div className="px-4 pb-4 pt-[calc(1rem+var(--lp-top))] safe-bottom animate-fade-in">
        <AmlReportCard result={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div className="px-4 pb-4 pt-[calc(1rem+var(--lp-top))] safe-bottom animate-fade-in" data-testid="aml-history-page">
      {showInlineBack && (
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t("common.back") || "Назад"}
        </button>
      )}

      <h1 className="text-xl font-bold mb-4">{t("aml.history") || "Мои проверки"}</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div role="alert" className="text-center text-sm py-8">
          <p>{i18n.language?.startsWith("ru") ? "Не удалось загрузить историю проверок" : "Could not load check history"}</p>
          <button className="mt-3 underline" onClick={() => setRetry(x => x + 1)}>{t("common.try_again")}</button>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-12">
          {t("aml.history_empty") || "Проверок пока нет"}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const zone = zoneOf(it.risk_percent, (it.hard_signals || []).length > 0, it.decision);
            return (
              <button
                key={it.uuid || `${it.address}-${it.created_at}`}
                onClick={() => { setSelected(it); haptic("light"); }}
                className="lp-press lp-glass w-full p-3 flex items-center gap-3 text-left"
              >
                <ZoneIcon zone={zone} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{short(it.address)}</p>
                  <p className="text-xs text-muted-foreground">
                    {(it.network || "").toUpperCase()} · {fmt(it.created_at)}
                  </p>
                </div>
                <span className="text-sm font-bold">
                  {it.risk_percent == null ? "—" : `${it.risk_percent}%`}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AmlHistoryPage;
